/**
 * The economy systems (doc 04, wages-at-the-whistle rework 2026-08-24): grid
 * connectivity, the static power solve, and the per-ROUND shift settlement.
 *
 * Nothing here accrues on wall-clock time — that is the whole point. The
 * roster of working buildings locks when the Lure is flashed; wages settle
 * when the wave clears, for roster members still standing. Waiting earns
 * exactly zero, for everyone. Pure functions over a narrow host (doc 12).
 */

import { CAPACITOR_SHIFT_FACTOR } from './const.ts';
import {
  CAPACITOR_MAX,
  CAPACITOR_RATE,
  CORE_POWER,
  GENERATOR_BURN,
  GENERATOR_POWER,
  GRID_RADIUS,
  PLACEABLE,
  RES_IDS,
  STORAGE_BASE,
  STORAGE_PER_DEPOT,
  type EconStatus,
} from './defs.ts';
import type { SimGrid } from './grid.ts';
import type { Resources, RunModifiers, SimEvent, SimState, Structure } from './types.ts';

/** What the economy needs from the sim — nothing more. */
export interface EconomyHost {
  state: SimState;
  grid: SimGrid;
  connected: Set<number>;
  power: { supply: number; demand: number; efficiency: number };
  status: Map<number, EconStatus>;
  storageCap: number;
  /** Meta numbers for this run (doc 08) — storage caps read the multiplier. */
  mods: RunModifiers;
  emit(ev: SimEvent): void;
}

export function structCentre(st: Structure): { x: number; y: number } {
  return { x: st.i + (st.size - 1) / 2, y: st.j + (st.size - 1) / 2 };
}

/**
 * Doc 04 connectivity: Core and Generators emit radius 5, Pylons relay
 * radius 5; anything else is connected if it stands within radius of an
 * emitter or a connected pylon. One global power pool — the grid decides who
 * may DRAW, not where the watts flow.
 */
export function recomputeConnectivity(host: EconomyHost): void {
  host.connected.clear();
  const live = host.state.structures.filter((x) => !x.ruin);
  const emitters = live.filter((x) => x.def === 'core_dome' || x.def === 'generator');
  const pylons = live.filter((x) => x.def === 'power_pylon');
  const nodes = emitters.map((x) => ({ st: x, on: true }));
  for (const p of pylons) nodes.push({ st: p, on: false });
  const inRange = (a: Structure, b: Structure): boolean => {
    const ca = structCentre(a);
    const cb = structCentre(b);
    const dx = ca.x - cb.x;
    const dy = ca.y - cb.y;
    return Math.sqrt(dx * dx + dy * dy) <= GRID_RADIUS;
  };
  for (let grew = true; grew; ) {
    grew = false;
    for (const n of nodes) {
      if (n.on) continue;
      if (nodes.some((m) => m.on && inRange(m.st, n.st))) {
        n.on = true;
        grew = true;
      }
    }
  }
  for (const st of live) {
    if (st.def === 'core_dome' || st.def === 'generator') {
      host.connected.add(st.id);
      continue;
    }
    if (nodes.some((m) => m.on && inRange(m.st, st))) host.connected.add(st.id);
  }
}

/**
 * Towers that draw watts (doc 05: Zappy/Laser/Fan feed themselves off the grid).
 * They are NOT on the wage roster — they earn nothing — but they are absolutely
 * on the demand side of the power solve.
 */
export function powerDrinkers(host: EconomyHost): Structure[] {
  const out: Structure[] = [];
  for (const st of host.state.structures) {
    const def = PLACEABLE[st.def];
    if (!def || st.ruin || def.kind !== 'tower' || def.power >= 0) continue;
    if (!host.connected.has(st.id)) continue;
    out.push(st);
  }
  return out;
}

/** Ids of the buildings that would work a shift pulled RIGHT NOW (the roster). */
export function shiftRoster(host: EconomyHost): number[] {
  const roster: number[] = [];
  for (const st of host.state.structures) {
    const def = PLACEABLE[st.def];
    if (!def || st.ruin || def.kind !== 'building') continue;
    if (st.def === 'power_pylon' || st.def === 'storage_depot') continue; // not wage-earners
    if (st.def !== 'generator' && !host.connected.has(st.id)) continue; // ⚡✗ works nothing
    roster.push(st.id);
  }
  return roster;
}

/**
 * The static power solve over a set of structures: supply from the Core and
 * fueled Generators, demand from connected consumers, uniform efficiency.
 * Pure — burns nothing.
 */
export function solvePower(
  host: EconomyHost,
  members: Structure[],
  fuelStock: number,
): { supply: number; demand: number; efficiency: number; fuelFrac: number; gens: number } {
  const gens = members.filter((x) => x.def === 'generator').length;
  const burnWant = GENERATOR_BURN * gens;
  const fuelFrac = burnWant > 0 ? Math.min(1, fuelStock / burnWant) : 0;
  const supply = CORE_POWER + GENERATOR_POWER * gens * fuelFrac;
  let demand = 0;
  for (const st of members) {
    const def = PLACEABLE[st.def]!;
    if (def.power < 0) demand += -def.power;
  }
  const efficiency = demand > 0 ? Math.min(1, supply / demand) : 1;
  return { supply, demand, efficiency, fuelFrac, gens };
}

/**
 * Settle one shift (doc 04): the wave's locked roster, minus the fallen, gets
 * paid once. Extraction drains deposits, chains convert in roster order, the
 * Generators burn their round of fuel, unused supply charges the capacitor.
 * With `commit=false` this is the payday FORECAST — same maths, no mutation.
 */
export function settleShift(host: EconomyHost, rosterIds: number[], commit: boolean): Resources {
  const s = host.state;
  const wages: Resources = { ore: 0, alloy: 0, ammo: 0, gas: 0, fuel: 0 };
  const survivors = rosterIds
    .map((id) => s.structures.find((x) => x.id === id))
    .filter((x): x is Structure => x !== undefined && !x.ruin);

  // work on scratch copies; write back only when committing
  const stock: Resources = { ...s.resources };
  const deposits: Record<string, number> = { ...s.deposits };
  const cap = host.storageCap;

  const power = solvePower(host, survivors, stock.fuel);
  const eff = power.efficiency;
  const burn = GENERATOR_BURN * power.gens * power.fuelFrac;
  stock.fuel = Math.max(0, stock.fuel - burn);
  wages.fuel -= burn;

  for (const st of survivors) {
    const def = PLACEABLE[st.def]!;
    if (def.wants && def.produces) {
      // extractor: one round's draw from the deposit under its feet
      const [res, perRound] = Object.entries(def.produces)[0]! as [keyof Resources, number];
      let want = Math.min(perRound * eff, Math.max(0, cap - stock[res]));
      for (const t of st.depositTiles) {
        if (want <= 0) break;
        const left = deposits[String(t)] ?? 0;
        const take = Math.min(want, left);
        if (take <= 0) continue;
        deposits[String(t)] = left - take;
        stock[res] += take;
        wages[res] += take;
        want -= take;
      }
    } else if (def.consumes && def.produces) {
      // converter: one round's conversion, capped by input stock and room
      const [inRes, inPerRound] = Object.entries(def.consumes)[0]! as [keyof Resources, number];
      const [outRes, outPerRound] = Object.entries(def.produces)[0]! as [keyof Resources, number];
      const room = Math.max(0, cap - stock[outRes]);
      const inByRoom = (room * inPerRound) / outPerRound;
      const actualIn = Math.min(inPerRound * eff, stock[inRes], inByRoom);
      if (actualIn > 0) {
        const out = (actualIn * outPerRound) / inPerRound;
        stock[inRes] -= actualIn;
        stock[outRes] += out;
        wages[inRes] -= actualIn;
        wages[outRes] += out;
      }
    }
  }

  if (commit) {
    // Repair Bays work the same shift: each restores up to its per-round rate
    // across damaged survivors, the dome first (doc 04)
    let healBudget = 0;
    for (const st of survivors) {
      healBudget += (PLACEABLE[st.def]!.healRatePerRound ?? 0) * eff;
    }
    if (healBudget > 0) {
      const wounded = s.structures
        .filter((x) => !x.ruin && x.hp < x.maxHp)
        .sort((a, b) => (a.kind === 'core' ? -1 : b.kind === 'core' ? 1 : a.id - b.id));
      for (const st of wounded) {
        if (healBudget <= 0) break;
        const heal = Math.min(healBudget, st.maxHp - st.hp);
        st.hp += heal;
        healBudget -= heal;
      }
    }
    // wages pay in whole units — the fractional dust stays with the paymaster
    for (const r of RES_IDS) stock[r] = Math.floor(stock[r] + 1e-9);
    s.resources = stock;
    s.deposits = deposits;
    // unused supply charges the Core Capacitor, once per round (doc 04/07)
    const surplus = Math.max(0, power.supply - power.demand);
    s.capacitor = Math.min(
      CAPACITOR_MAX,
      s.capacitor + Math.min(CAPACITOR_RATE, surplus) * CAPACITOR_SHIFT_FACTOR,
    );
  }
  return wages;
}

/**
 * Per-tick bookkeeping with NO accrual: storage caps, the static power
 * preview (HUD, brownout flicker), and each building's readiness status.
 */
export function refreshEconomyStatus(host: EconomyHost): void {
  const s = host.state;
  host.status.clear();

  const depots = s.structures.filter(
    (x) => x.def === 'storage_depot' && !x.ruin && host.connected.has(x.id),
  );
  host.storageCap = Math.round(
    (STORAGE_BASE + STORAGE_PER_DEPOT * depots.length) * host.mods.storageMult,
  );

  const roster = shiftRoster(host)
    .map((id) => s.structures.find((x) => x.id === id)!)
    .filter((x) => x !== undefined);
  const prevEff = host.power.efficiency;
  const power = solvePower(host, [...roster, ...powerDrinkers(host)], s.resources.fuel);
  host.power = { supply: power.supply, demand: power.demand, efficiency: power.efficiency };
  if (prevEff >= 1 && power.efficiency < 1) host.emit({ type: 'brownout', on: true });
  if (prevEff < 1 && power.efficiency >= 1) host.emit({ type: 'brownout', on: false });

  for (const st of s.structures) {
    const def = PLACEABLE[st.def];
    if (!def || st.ruin || def.kind !== 'building') continue;
    if (st.def === 'generator') {
      host.status.set(st.id, power.fuelFrac > 0 ? 'working' : 'stalled');
      continue;
    }
    if (!host.connected.has(st.id)) {
      host.status.set(st.id, 'offgrid');
      continue;
    }
    if (st.def === 'power_pylon' || st.def === 'storage_depot') {
      host.status.set(st.id, 'working');
      continue;
    }
    // ready = it would move goods next settlement: deposit left / input in stock
    let ready = false;
    if (def.wants) {
      ready = st.depositTiles.some((t) => (s.deposits[String(t)] ?? 0) > 0);
    } else if (def.consumes) {
      const [inRes] = Object.entries(def.consumes)[0]! as [keyof Resources, number];
      ready = s.resources[inRes] > 0;
    }
    host.status.set(st.id, ready ? 'working' : 'stalled');
  }
}
